import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { IPayrollSheetApprovalAccessKey } from "@/payroll/payroll.d";
import { ApprovalMark } from "./approval-mark";
import { ApprovalToggle } from "./approval-toggle";
import { APPROVAL_GATES } from "./approval-types";
import { GroupApprovalProgress } from "./group-approval-progress";
import type { ApprovalPanelProps } from "./types";
import { getSheetSummary } from "./utils";

const GATE_COLUMN_CLASSNAME = "flex w-27 shrink-0 items-center justify-end";

/**
 * Sign-off across the whole period — one approval unit per row and one gate per
 * column, so the workbook's state reads as a compact matrix.
 */
export const PeriodApprovalPanel = ({
  accessKeys,
  pendingApproval,
  onApprovalChange,
}: ApprovalPanelProps & { accessKeys: IPayrollSheetApprovalAccessKey[] }) => {
  const hasUnauthorized = accessKeys.some((key) => !key.approval_authorized);

  return (
    <Stack gap={0} align="stretch" className="w-full min-w-0">
      <Stack
        gap={2}
        align="stretch"
        className="w-full border-b border-line px-4 py-3"
      >
        <Text variant="label">Approvals for this pay period</Text>
        <Stack className="w-full" direction="row" align="center" gap={2}>
          <span className="min-w-0 flex-1" />
          {APPROVAL_GATES.map((gate) => (
            <span
              key={gate.type}
              className={cn(GATE_COLUMN_CLASSNAME, "gap-1")}
            >
              <gate.Icon
                aria-hidden="true"
                className="h-3.5 w-3.5 shrink-0 text-subtle"
              />
              <Text
                variant="caption"
                tone="muted"
                className="whitespace-nowrap"
              >
                {gate.label}
              </Text>
            </span>
          ))}
        </Stack>
      </Stack>

      <Stack
        gap={0}
        align="stretch"
        className="max-h-80 w-full overflow-y-auto"
      >
        {accessKeys.map((accessKey) => {
          const sheetSummary = getSheetSummary(accessKey);

          return (
            <Stack
              key={accessKey.id}
              direction="row"
              align="center"
              gap={2}
              className={cn(
                "w-full",
                "border-b border-line px-4 py-2.5 last:border-b-0",
                "transition-colors hover:bg-neutral-subtle-hovered",
              )}
            >
              <Stack
                direction="row"
                align="center"
                gap={2}
                className="min-w-0 flex-1"
              >
                <ApprovalMark accessKey={accessKey} />
                <Stack gap={0} align="stretch" className="w-full min-w-0">
                  <Text
                    variant="caption"
                    className="truncate font-semibold"
                    title={accessKey.name}
                  >
                    {accessKey.name}
                  </Text>
                  {sheetSummary && (
                    <Text
                      variant="caption"
                      tone="muted"
                      className="truncate"
                      title={sheetSummary}
                    >
                      {sheetSummary}
                    </Text>
                  )}
                </Stack>
              </Stack>

              {APPROVAL_GATES.map((gate) => (
                <span key={gate.type} className={GATE_COLUMN_CLASSNAME}>
                  <Stack direction="row" align="center" justify="end" gap={1}>
                    {accessKey.approval_mode === "sheet" &&
                      (accessKey.approvals[gate.type].total_count ?? 0) > 1 && (
                        <GroupApprovalProgress
                          accessKey={accessKey}
                          approvalType={gate.type}
                        />
                      )}
                    <ApprovalToggle
                      gate={gate}
                      subject={accessKey}
                      approvalAuthorized={accessKey.approval_authorized}
                      isBusy={Boolean(pendingApproval)}
                      isPending={
                        pendingApproval?.accessKeyId === accessKey.id &&
                        !pendingApproval.sheetId &&
                        pendingApproval.approvalType === gate.type
                      }
                      onChange={
                        onApprovalChange
                          ? (enabled) =>
                              onApprovalChange(accessKey, gate.type, enabled)
                          : undefined
                      }
                    />
                  </Stack>
                </span>
              ))}
            </Stack>
          );
        })}
      </Stack>

      <Text
        variant="caption"
        tone="muted"
        className="border-t border-line px-4 py-2.5"
      >
        {hasUnauthorized
          ? "Greyed out switches are groups you can see but can't approve."
          : "Use these switches to approve whole groups, or approve each sheet from its toolbar."}
      </Text>
    </Stack>
  );
};
